import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Row, Col, Badge, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

const PAYMENT_OPTIONS = ['Cash', 'Card', 'BankTransfer', 'Invoiced'];

const parseDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toISOString().split('T')[0];
};

const compressImage = (file, { maxWidth = 800, maxHeight = 600, quality = 0.8 } = {}) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = event.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const booleanFromInput = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return false;
};

const DriverProfileAdminModal = ({ show, user, onHide, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const logoFileRef = useRef(null);
  const carPhotoFileRef = useRef(null);
  const driverPhotoFileRef = useRef(null);

  useEffect(() => {
    if (show && user) {
      setForm({
        name: user.name || '',
        firstName: user.firstName || '',
        surname: user.surname || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        subscriptionStatus: user.subscriptionStatus || 'inactive',
        role: user.role || 'professional',
        companyName: user.companyName || '',
        companyAddress: user.companyAddress || '',
        website: user.website || '',
        carMake: user.carMake || '',
        carModel: user.carModel || '',
        carSeats: user.carSeats || '',
        regNumber: user.regNumber || '',
        licensingAuthority: user.licensingAuthority || '',
        badgeNumber: user.badgeNumber || '',
        licenseType: user.licenseType || 'Hackney License',
        mainPostcode: user.mainPostcode || '',
        postcodeSectorsCovered: Array.isArray(user.postcodeSectorsCovered)
          ? user.postcodeSectorsCovered.join(', ')
          : user.postcodeSectorsCovered || '',
        logo: user.logo || '',
        photoOfCar: user.photoOfCar || '',
        photoOfDriver: user.photoOfDriver || '',
        accountType: user.accountType || 'Personal',
        accountName: user.accountName || '',
        accountSortCode: user.accountSortCode || '',
        accountNumber: user.accountNumber || '',
        paymentMethods: Array.isArray(user.paymentMethods) && user.paymentMethods.length
          ? user.paymentMethods
          : ['Cash'],
        leadEmailOptIn: booleanFromInput(user.leadEmailOptIn),
        emailDiaryDaily: booleanFromInput(user.emailDiaryDaily),
        dispatchAccessAccepted: booleanFromInput(user.dispatchAccessAccepted),
        dispatchEmail: user.dispatchEmail || '',
        audioNotifications: user.audioNotifications !== false,
        roadTaxExpiry: parseDate(user.roadTaxExpiry),
        motExpiry: parseDate(user.motExpiry),
        insuranceExpiry: parseDate(user.insuranceExpiry),
        vehicleInspectionExpiry: parseDate(user.vehicleInspectionExpiry)
      });
    } else {
      setForm(null);
    }
  }, [show, user]);

  const paymentChoices = useMemo(() => {
    return PAYMENT_OPTIONS.map((option) => ({
      value: option,
      label: option === 'BankTransfer' ? 'Bank Transfer' : option === 'Invoiced' ? 'Invoiced Accounts' : option
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setForm((prev) => ({ ...prev, [name]: newValue }));
  };

  const handlePaymentToggle = (method) => {
    setForm((prev) => {
      const current = prev.paymentMethods || [];
      const exists = current.includes(method);
      const next = exists ? current.filter((m) => m !== method) : [...current, method];
      return { ...prev, paymentMethods: next.length ? next : ['Cash'] };
    });
  };

  const handleImageSelection = async (file, field, options) => {
    if (!file) return;
    try {
      const dataUrl = await compressImage(file, options);
      setForm((prev) => ({ ...prev, [field]: dataUrl }));
    } catch (err) {
      console.error('Image processing failed', err);
      toast.error('Failed to process image. Please try a different file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form || !user) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const postcodeArray = form.postcodeSectorsCovered
        ? form.postcodeSectorsCovered
            .split(',')
            .map((code) => code.trim())
            .filter((code) => code.length > 0)
        : undefined;

      const payload = {
        name: form.name,
        firstName: form.firstName,
        surname: form.surname,
        email: form.email,
        phoneNumber: form.phoneNumber,
        subscriptionStatus: form.subscriptionStatus,
        role: form.role,
        companyName: form.companyName,
        companyAddress: form.companyAddress,
        website: form.website,
        carMake: form.carMake,
        carModel: form.carModel,
        carSeats: form.carSeats,
        regNumber: form.regNumber,
        licensingAuthority: form.licensingAuthority,
        badgeNumber: form.badgeNumber,
        licenseType: form.licenseType,
        mainPostcode: form.mainPostcode,
        postcodeSectorsCovered: postcodeArray,
        logo: form.logo,
        photoOfCar: form.photoOfCar,
        photoOfDriver: form.photoOfDriver,
        accountType: form.accountType,
        accountName: form.accountName,
        accountSortCode: form.accountSortCode,
        accountNumber: form.accountNumber,
        paymentMethods: form.paymentMethods,
        leadEmailOptIn: !!form.leadEmailOptIn,
        emailDiaryDaily: !!form.emailDiaryDaily,
        dispatchAccessAccepted: !!form.dispatchAccessAccepted,
        dispatchEmail: form.dispatchEmail,
        audioNotifications: !!form.audioNotifications,
        roadTaxExpiry: form.roadTaxExpiry || null,
        motExpiry: form.motExpiry || null,
        insuranceExpiry: form.insuranceExpiry || null,
        vehicleInspectionExpiry: form.vehicleInspectionExpiry || null
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${user._id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success('Driver profile updated successfully');
      onSaved?.(response.data);
      onHide();
    } catch (err) {
      console.error('Failed to update driver profile', err);
      const message = err.response?.data?.error || 'Failed to update driver profile';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" fullscreen="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Driver Profile</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {!form ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <section className="mb-4">
                <h5 className="text-primary border-bottom pb-2">👤 Basic Information</h5>
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Name</Form.Label>
                      <Form.Control name="name" value={form.name} onChange={handleChange} placeholder="Display name" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control type="email" name="email" value={form.email} onChange={handleChange} required />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control name="phoneNumber" value={form.phoneNumber} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Subscription Status</Form.Label>
                      <Form.Select name="subscriptionStatus" value={form.subscriptionStatus} onChange={handleChange}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                        <option value="suspended">Suspended</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Role</Form.Label>
                      <Form.Select name="role" value={form.role} onChange={handleChange}>
                        <option value="professional">Professional</option>
                        <option value="listing_only">Listing Only</option>
                        <option value="dispatch">Dispatch</option>
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <section className="mb-4">
                <h5 className="text-primary border-bottom pb-2">🏢 Company Information</h5>
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Company Name</Form.Label>
                      <Form.Control name="companyName" value={form.companyName} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Company Address</Form.Label>
                      <Form.Control name="companyAddress" value={form.companyAddress} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Website</Form.Label>
                      <Form.Control name="website" value={form.website} onChange={handleChange} placeholder="https://" />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <section className="mb-4">
                <h5 className="text-primary border-bottom pb-2">🚗 Vehicle Information</h5>
                <Row className="g-3 mt-1">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Car Make</Form.Label>
                      <Form.Control name="carMake" value={form.carMake} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Car Model</Form.Label>
                      <Form.Control name="carModel" value={form.carModel} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Seats</Form.Label>
                      <Form.Control name="carSeats" value={form.carSeats} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Vehicle Registration</Form.Label>
                      <Form.Control name="regNumber" value={form.regNumber} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>License Type</Form.Label>
                      <Form.Select name="licenseType" value={form.licenseType} onChange={handleChange}>
                        <option value="Hackney License">Hackney License</option>
                        <option value="Private Hire">Private Hire</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Licensing Authority</Form.Label>
                      <Form.Control name="licensingAuthority" value={form.licensingAuthority} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Badge Number</Form.Label>
                      <Form.Control name="badgeNumber" value={form.badgeNumber} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <section className="mb-4">
                <h5 className="text-primary border-bottom pb-2">📸 Branding & Photos</h5>
                <Row className="g-3 mt-1">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Logo</Form.Label>
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Control name="logo" value={form.logo} onChange={handleChange} placeholder="Paste URL or upload" />
                        <input
                          ref={logoFileRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleImageSelection(file, 'logo', { maxWidth: 400, maxHeight: 300, quality: 0.8 });
                              e.target.value = '';
                            }
                          }}
                          style={{ display: 'none' }}
                        />
                        <Button variant="outline-secondary" onClick={() => logoFileRef.current?.click()}>
                          Upload
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Vehicle Photo</Form.Label>
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Control name="photoOfCar" value={form.photoOfCar} onChange={handleChange} placeholder="Paste URL or upload" />
                        <input
                          ref={carPhotoFileRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleImageSelection(file, 'photoOfCar', { maxWidth: 800, maxHeight: 600, quality: 0.7 });
                              e.target.value = '';
                            }
                          }}
                          style={{ display: 'none' }}
                        />
                        <Button variant="outline-secondary" onClick={() => carPhotoFileRef.current?.click()}>
                          Upload
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Driver Photo</Form.Label>
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Control name="photoOfDriver" value={form.photoOfDriver} onChange={handleChange} placeholder="Paste URL or upload" />
                        <input
                          ref={driverPhotoFileRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleImageSelection(file, 'photoOfDriver', { maxWidth: 600, maxHeight: 600, quality: 0.8 });
                              e.target.value = '';
                            }
                          }}
                          style={{ display: 'none' }}
                        />
                        <Button variant="outline-secondary" onClick={() => driverPhotoFileRef.current?.click()}>
                          Upload
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <section className="mb-4">
                <h5 className="text-primary border-bottom pb-2">🗺️ Coverage Area</h5>
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Main Postcode</Form.Label>
                      <Form.Control name="mainPostcode" value={form.mainPostcode} onChange={handleChange} placeholder="e.g., EX12" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Postcode Sectors Covered</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="postcodeSectorsCovered"
                        value={form.postcodeSectorsCovered}
                        onChange={handleChange}
                        placeholder="Comma separated e.g., EX12, EX13, DT6"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <section className="mb-4">
                <h5 className="text-primary border-bottom pb-2">📅 Document Expiry Dates</h5>
                <Row className="g-3 mt-1">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Road Tax</Form.Label>
                      <Form.Control type="date" name="roadTaxExpiry" value={form.roadTaxExpiry} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>MOT</Form.Label>
                      <Form.Control type="date" name="motExpiry" value={form.motExpiry} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Insurance</Form.Label>
                      <Form.Control type="date" name="insuranceExpiry" value={form.insuranceExpiry} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Vehicle Inspection</Form.Label>
                      <Form.Control type="date" name="vehicleInspectionExpiry" value={form.vehicleInspectionExpiry} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <section className="mb-4">
                <h5 className="text-primary border-bottom pb-2">💳 Bank & Payment</h5>
                <Row className="g-3 mt-1">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Account Type</Form.Label>
                      <Form.Select name="accountType" value={form.accountType} onChange={handleChange}>
                        <option value="Personal">Personal</option>
                        <option value="Business">Business</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Account Name</Form.Label>
                      <Form.Control name="accountName" value={form.accountName} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Sort Code</Form.Label>
                      <Form.Control name="accountSortCode" value={form.accountSortCode} onChange={handleChange} placeholder="00-00-00" />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Account Number</Form.Label>
                      <Form.Control name="accountNumber" value={form.accountNumber} onChange={handleChange} placeholder="********" />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="mt-3">
                  <Form.Label>Accepted Payment Methods</Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    {paymentChoices.map((option) => {
                      const isActive = form.paymentMethods?.includes(option.value);
                      return (
                        <Form.Check
                          key={option.value}
                          type="switch"
                          id={`payment-${option.value}`}
                          label={option.label}
                          checked={isActive}
                          onChange={() => handlePaymentToggle(option.value)}
                        />
                      );
                    })}
                  </div>
                </div>
              </section>

              <section className="mb-4">
                <h5 className="text-primary border-bottom pb-2">🔔 Preferences & Dispatch</h5>
                <Row className="g-3 mt-1">
                  <Col md={4}>
                    <Form.Check
                      type="switch"
                      id="leadEmailOptIn"
                      name="leadEmailOptIn"
                      label="Receive Lead Emails"
                      checked={form.leadEmailOptIn}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Check
                      type="switch"
                      id="emailDiaryDaily"
                      name="emailDiaryDaily"
                      label="Daily Diary Email"
                      checked={form.emailDiaryDaily}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Check
                      type="switch"
                      id="audioNotifications"
                      name="audioNotifications"
                      label="Audio Notifications"
                      checked={form.audioNotifications}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>
                <Row className="g-3 mt-2">
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="dispatchAccessAccepted"
                      name="dispatchAccessAccepted"
                      label="Dispatch Access Accepted"
                      checked={form.dispatchAccessAccepted}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Dispatch Email</Form.Label>
                      <Form.Control name="dispatchEmail" value={form.dispatchEmail} onChange={handleChange} placeholder="dispatcher@example.com" />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <section className="mb-3">
                <h6>Status Overview</h6>
                <div className="d-flex gap-3 flex-wrap">
                  <Badge bg={user.profileCompleted ? 'success' : 'secondary'}>Profile {user.profileCompleted ? 'Complete' : 'Incomplete'}</Badge>
                  <Badge bg={user.licensingSubmitted ? 'info' : 'secondary'}>Licensing {user.licensingSubmitted ? 'Submitted' : 'Pending'}</Badge>
                  <Badge bg={user.licensingApproved ? 'success' : 'warning'}>Licensing {user.licensingApproved ? 'Approved' : 'Awaiting Review'}</Badge>
                </div>
              </section>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving || !form}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

DriverProfileAdminModal.propTypes = {
  show: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string
  }),
  onHide: PropTypes.func.isRequired,
  onSaved: PropTypes.func
};

DriverProfileAdminModal.defaultProps = {
  user: null,
  onSaved: undefined
};

export default DriverProfileAdminModal;
